import { Download, ExternalLink, Image as ImageIcon, MonitorSmartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../components/ui/ScreenPrimitives';
import { getBugReport, getBugReportScreenshot } from '../lib/bugReports';

function formatDate(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatBytes(value) {
  if (!Number.isFinite(value)) return 'Unknown size';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function BugReportPage() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [status, setStatus] = useState({ type: 'loading', message: 'Loading private report...' });
  const [downloadStatus, setDownloadStatus] = useState({ type: 'idle', message: '' });

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    async function load() {
      try {
        const nextReport = await getBugReport(reportId);
        if (!active) return;
        setReport(nextReport);

        if (
          nextReport.hasScreenshot &&
          ['image/jpeg', 'image/png', 'image/webp'].includes(nextReport.screenshot?.contentType)
        ) {
          const screenshot = await getBugReportScreenshot(reportId);
          objectUrl = URL.createObjectURL(screenshot);
          if (active) setScreenshotUrl(objectUrl);
        }
        if (active) setStatus({ type: 'success', message: 'Private report loaded' });
      } catch (error) {
        if (active) {
          setStatus({
            type: 'error',
            message: error.message || 'Could not load this bug report.',
          });
        }
      }
    }

    void load();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId]);

  async function downloadOriginal() {
    setDownloadStatus({ type: 'loading', message: 'Preparing download...' });
    try {
      const screenshot = await getBugReportScreenshot(reportId, { download: true });
      const objectUrl = URL.createObjectURL(screenshot);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = report?.screenshot?.originalFileName || 'bug-screenshot';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      setDownloadStatus({ type: 'success', message: 'Download started' });
    } catch (error) {
      setDownloadStatus({
        type: 'error',
        message: error.message || 'Could not download the screenshot.',
      });
    }
  }

  const githubTone =
    report?.github?.state === 'created'
      ? 'success'
      : report?.github?.state === 'failed'
        ? 'error'
        : 'loading';

  return (
    <div className="grid gap-6">
      <PageHero
        eyebrow="Private bug evidence"
        title={report ? `Report ${report.reportId}` : 'Bug report'}
        lede="Review the sanitized user report and its private Azure-hosted screenshot."
        action={<StatusPill tone={status.type}>{status.message}</StatusPill>}
      />

      {report ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <MetricCard
              label="GitHub"
              value={report.github?.state || 'pending'}
              note={
                report.github?.issueNumber
                  ? `Issue #${report.github.issueNumber}`
                  : 'Waiting for issue creation'
              }
              icon={ExternalLink}
            />
            <MetricCard
              label="Platform"
              value={report.app?.platform || 'Unknown'}
              note={`${report.app?.name || 'Little Adda'} ${report.app?.version || 'unknown'}`}
              icon={MonitorSmartphone}
            />
            <MetricCard
              label="Screenshot"
              value={report.hasScreenshot ? 'Attached' : 'None'}
              note={report.screenshot ? formatBytes(report.screenshot.sizeBytes) : 'No image provided'}
              icon={ImageIcon}
            />
          </div>

          <SectionCard>
            <SectionTitle
              title="User report"
              copy={`Submitted ${formatDate(report.createdAt)}`}
              action={<StatusPill tone={githubTone}>{report.github?.state || 'pending'}</StatusPill>}
            />
            <p className="mt-6 whitespace-pre-wrap text-base leading-8 text-app-ink">
              {report.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-app-muted">
              <span className="rounded-full border border-app-border bg-app-surface-alt px-3 py-1.5">
                {report.app?.platform} {report.app?.platformVersion}
              </span>
              <span className="rounded-full border border-app-border bg-app-surface-alt px-3 py-1.5">
                {report.app?.name} {report.app?.version}
              </span>
            </div>
            {report.github?.issueUrl ? (
              <a
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition-transform duration-150 active:scale-[0.97]"
                href={report.github.issueUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open GitHub issue
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
            ) : null}
          </SectionCard>

          {report.hasScreenshot ? (
            <SectionCard>
              <SectionTitle
                title="Screenshot"
                copy={
                  report.screenshot
                    ? `${report.screenshot.originalFileName} · ${formatBytes(report.screenshot.sizeBytes)}`
                    : 'Private Azure attachment'
                }
                action={
                  <button
                    className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition-transform duration-150 active:scale-[0.97]"
                    type="button"
                    onClick={downloadOriginal}
                    disabled={downloadStatus.type === 'loading'}
                  >
                    <Download className="h-4 w-4" aria-hidden="true" />
                    {downloadStatus.type === 'loading' ? 'Preparing...' : 'Download original'}
                  </button>
                }
              />
              {screenshotUrl ? (
                <div className="mt-6 overflow-hidden rounded-[20px] border border-app-border bg-app-surface-alt p-3">
                  <img
                    className="max-h-[70vh] w-full rounded-2xl object-contain"
                    src={screenshotUrl}
                    alt="User-submitted bug screenshot"
                  />
                </div>
              ) : ['image/heic', 'image/heif'].includes(report.screenshot?.contentType) ? (
                <p className="mt-6 text-sm text-app-muted">
                  This image format may not preview in your browser. Download the original to view it.
                </p>
              ) : (
                <p className="mt-6 text-sm text-app-muted">Loading screenshot...</p>
              )}
              {downloadStatus.message ? (
                <p className="mt-4 text-sm text-app-muted">{downloadStatus.message}</p>
              ) : null}
            </SectionCard>
          ) : null}
        </>
      ) : status.type === 'error' ? (
        <SectionCard>
          <SectionTitle
            title="Report unavailable"
            copy="Your account may not be included in the backend bug-report staff allowlist."
          />
        </SectionCard>
      ) : null}
    </div>
  );
}

export default BugReportPage;
